/**
 * AttachmentPreview leaf — renders a single email attachment.
 * Extracted verbatim from ContactEditModal.tsx (PR5 Phase 5).
 */
import { useState, useEffect } from 'react';
import { getAuthItem } from '@/lib/authStorage';
import { emailApiBase } from '@/lib/email-api';

/** Renders a single email attachment — images/PDFs load inline, other files show download button */
export function AttachmentPreview({ att, messageId, isImage }: { att: { filename: string; mimeType: string; size?: number; attachmentId: string }; messageId: string; isImage: boolean }) {
  const isPdf = att.mimeType === 'application/pdf';
  const shouldPreview = isImage || isPdf;
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(shouldPreview);

  useEffect(() => {
    if (!shouldPreview) return;
    let revoke = '';
    (async () => {
      try {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        const authToken = getAuthItem('xavior_auth_token');
        if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
        const res = await fetch(`${emailApiBase()}/attachment`, {
          method: 'POST', headers,
          body: JSON.stringify({ messageId, attachmentId: att.attachmentId, filename: att.filename, mimeType: att.mimeType }),
        });
        if (!res.ok) throw new Error('Failed');
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        revoke = url;
        setBlobUrl(url);
      } catch { /* ignore */ }
      setLoading(false);
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [messageId, att.attachmentId]);

  if (shouldPreview) {
    if (loading) return <div style={{ padding: 8, fontSize: 12, color: 'var(--color-text-muted)' }}>Loading {att.filename}...</div>;
    if (blobUrl && isImage) return (
      <div>
        <img src={blobUrl} alt={att.filename} style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 8, cursor: 'pointer' }}
          onClick={() => window.open(blobUrl, '_blank')} />
        <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>{att.filename}{att.size ? ` (${(att.size / 1024).toFixed(0)}KB)` : ''}</div>
      </div>
    );
    if (blobUrl && isPdf) return (
      <div>
        <iframe
          src={blobUrl}
          style={{ width: '100%', height: 400, border: '1px solid #DADCE0', borderRadius: 8 }}
          title={att.filename}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{att.filename}{att.size ? ` (${(att.size / 1024).toFixed(0)}KB)` : ''}</span>
          <button type="button" onClick={() => {
            const a = document.createElement('a');
            a.href = blobUrl; a.download = att.filename; a.click();
          }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#1a9ba5', padding: 0 }}>
            Download
          </button>
        </div>
      </div>
    );
    return null;
  }

  return (
    <button
      type="button"
      onClick={async () => {
        try {
          const headers: Record<string, string> = { 'Content-Type': 'application/json' };
          const authToken = getAuthItem('xavior_auth_token');
          if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
          const res = await fetch(`${emailApiBase()}/attachment`, {
            method: 'POST', headers,
            body: JSON.stringify({ messageId, attachmentId: att.attachmentId, filename: att.filename, mimeType: att.mimeType }),
          });
          if (!res.ok) throw new Error('Download failed');
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = att.filename; a.click();
          URL.revokeObjectURL(url);
        } catch { /* silently fail */ }
      }}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '6px 10px', background: '#F1F3F4', border: '1px solid #DADCE0',
        borderRadius: 8, cursor: 'pointer', fontSize: 12, color: 'var(--color-text)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = '#E8E9EC'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = '#F1F3F4'; }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5f6368" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
      </svg>
      <span style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.filename}</span>
      {att.size && <span style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>({(att.size / 1024).toFixed(0)}KB)</span>}
    </button>
  );
}
